import {
  DATE_FORMAT,
  FIRST_DAY_OF_WEEK,
  NUMBER_FORMAT,
  WEEK_NUMBER_FORMAT,
  type DateFormat,
  type FirstDayOfWeek,
  type NumberFormat
} from '@gat-ship/types'
import { useLocalStorage } from '@gat-ship/utils'
import { globalUtils } from '@gat-ship/utils'
import { computed, toValue, watch, type MaybeRefOrGetter } from 'vue'

export interface UserPreferences {
  userId: string
  locale: string
  theme: 'light' | 'dark' | 'auto'
  modalType: 'drawer' | 'dialog'
  /** The date format to use throughout the app */
  dateFormat: DateFormat
  /** If the user input a single number, should it be interpreted as month or day? (used in date-edit when the date format is MM.DD.YYYY, MM/DD/YYYY, MM/DD/YY) */
  dateFormatInferFirstPartAs: 'month' | 'day'
  /** The week number format to use in calendars throughout the app */
  weekNumberFormat: string
  /** The first day of the week to use in calendars throughout the app */
  firstDayOfWeek: FirstDayOfWeek
  /** The number format to use in the app */
  numberFormat: NumberFormat
  /** Feature flags the user has explicitly opted out of */
  disabledFeatureFlags: string[]
}

const DEFAULT_USER_PREFERENCES: (userId: string) => UserPreferences = (
  userId
) => ({
  userId,
  locale: 'nb-NO',
  theme: 'auto',
  modalType: 'drawer',
  dateFormat: DATE_FORMAT['DD.MM.YYYY'],
  dateFormatInferFirstPartAs: 'day',
  weekNumberFormat: WEEK_NUMBER_FORMAT['ISO (first 4-day week)'].toString(),
  firstDayOfWeek: FIRST_DAY_OF_WEEK['Monday'],
  numberFormat: NUMBER_FORMAT['10,000.00'],
  disabledFeatureFlags: []
})

export function useUserPreferences(params: {
  userId: MaybeRefOrGetter<string>
}) {
  const theme = globalUtils.composables.useTheme()
  const userId = computed(() => toValue(params.userId))
  const userPreferences = useLocalStorage<UserPreferences[]>(
    'user-preferences',
    []
  )

  function setLocale(locale: string) {
    globalUtils.date.setCurrentLocale(locale)
  }

  function setNumberFormat(format: NumberFormat) {
    globalUtils.date.setCurrentNumberFormat(format)
  }

  const systemSettings = computed(() => {
    const defaultLocale =
      navigator.languages?.[0] || navigator.language || 'en-US'
    const locale = new Intl.Locale(defaultLocale)
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale
    }
  })

  watch(
    systemSettings,
    (value) => {
      setLocale(value.locale.toString())
    },
    {
      immediate: true,
      deep: true
    }
  )

  function _setUserPreferences(preferences: Omit<UserPreferences, 'userId'>) {
    const index = userPreferences.value.findIndex(
      (p) => p.userId === userId.value
    )
    if (index !== -1) {
      const existing = userPreferences.value[index]!
      const merged: UserPreferences = { ...existing, ...preferences }
      userPreferences.value[index] = merged
      setLocale(merged.locale)
      setNumberFormat(merged.numberFormat)
      return merged
    }
    const created: UserPreferences = { userId: userId.value, ...preferences }
    userPreferences.value.push(created)
    setLocale(preferences.locale)
    setNumberFormat(preferences.numberFormat)
    return created
  }

  async function fetchUserPreferences() {
    if (!userId.value) {
      console.warn('Unable to fetch user preferences. User ID not found')
    }
    const found = userPreferences.value.find((p) => p.userId === userId.value)
    if (!found) {
      _setUserPreferences(DEFAULT_USER_PREFERENCES(userId.value))
    } else {
      setLocale(found.locale)
      setNumberFormat(found.numberFormat)
    }

    const currentTheme = getUserPreferences().theme
    theme.setTheme(currentTheme)
  }

  function updateUserPreferences(
    preferences: Omit<Partial<UserPreferences>, 'userId'>
  ) {
    const originalTheme = getUserPreferences().theme

    const index = userPreferences.value.findIndex(
      (p) => p.userId === userId.value
    )
    if (index === -1) {
      return
    }
    const existing = userPreferences.value[index]!
    const merged: UserPreferences = { ...existing, ...preferences }
    userPreferences.value[index] = merged
    setLocale(merged.locale)
    setNumberFormat(merged.numberFormat)
    if (preferences.theme && originalTheme !== preferences.theme) {
      theme.setTheme(preferences.theme)
    }
  }

  function getUserPreferences() {
    const found = userPreferences.value.find((p) => p.userId === userId.value)
    if (!found) {
      return DEFAULT_USER_PREFERENCES(userId.value)
    }
    return found
  }

  return {
    systemSettings,
    updateUserPreferences,
    getUserPreferences,
    fetchUserPreferences
  }
}
